import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import environment from '../config/environment';

const DIAS_SEMANA = [
  { key: 'lunes', label: 'Lunes', corto: 'Lun' },
  { key: 'martes', label: 'Martes', corto: 'Mar' },
  { key: 'miercoles', label: 'Miércoles', corto: 'Mié' },
  { key: 'jueves', label: 'Jueves', corto: 'Jue' },
  { key: 'viernes', label: 'Viernes', corto: 'Vie' },
  { key: 'sabado', label: 'Sábado', corto: 'Sáb' },
  { key: 'domingo', label: 'Domingo', corto: 'Dom' }
];

const ORDEN_DIA = Object.fromEntries(DIAS_SEMANA.map((d, i) => [d.key, i]));

function codigoPuntoDisplay(idPunto) {
  if (idPunto == null || idPunto === '') return '';
  return `${environment.puntoChar || ''}${idPunto}`;
}

function etiquetaPuntoConCodigo(p) {
  // VIRTUAL: solo nombre legible (sin prefijo de código de proyecto)
  if (String(p?.origen_punto || '').toUpperCase() === 'VIRTUAL') {
    return p?.etiqueta || p?.id_generado || 'Punto virtual';
  }
  const codigo = codigoPuntoDisplay(p?.id_punto);
  const prefijo = codigo ? `${codigo}-` : '';
  return `${prefijo}${p?.etiqueta || ''}`;
}

function urlMapaVirtual(p) {
  if (String(p?.origen_punto || '').toUpperCase() !== 'VIRTUAL') return null;
  if (p?.maps_url) return String(p.maps_url);
  const lat = p?.latitud != null ? Number(p.latitud) : NaN;
  const lng = p?.longitud != null ? Number(p.longitud) : NaN;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return `https://maps.google.com/?q=${lat},${lng}`;
}

function IconoMapaVirtual({ url }) {
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center rounded-md border border-sky-200 bg-sky-50 p-1.5 text-sky-700 hover:bg-sky-100 shrink-0"
      title="Ver ubicación en Google Maps"
      aria-label="Ver ubicación en Google Maps"
      onClick={(e) => e.stopPropagation()}
    >
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
        <path
          fillRule="evenodd"
          d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
          clipRule="evenodd"
        />
      </svg>
    </a>
  );
}

function IconoTurno({ franja }) {
  const esNoche = franja === 'N';
  const titulo = esNoche ? 'Turno noche' : 'Turno día';
  return (
    <span
      className={`inline-flex items-center justify-center rounded-md border p-1 ${
        esNoche
          ? 'border-slate-600 bg-slate-700 text-amber-200'
          : 'border-amber-300 bg-amber-50 text-amber-600'
      }`}
      title={titulo}
      aria-label={titulo}
    >
      {esNoche ? (
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      ) : (
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
          <path
            fillRule="evenodd"
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </span>
  );
}

function compararTarjetas(a, b) {
  const da = ORDEN_DIA[a.dia_semana] ?? 99;
  const db = ORDEN_DIA[b.dia_semana] ?? 99;
  if (da !== db) return da - db;
  const fa = a.franja_asignada === 'D' ? 0 : 1;
  const fb = b.franja_asignada === 'D' ? 0 : 1;
  if (fa !== fb) return fa - fb;
  return String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es');
}

/** Una tarjeta por ruta + día + turno asignado al supervisor. */
function aplanarTarjetas(rutas) {
  const tarjetas = [];
  for (const ruta of rutas || []) {
    for (const bloque of ruta.bloques || []) {
      tarjetas.push({
        key: `${ruta.id}-${bloque.dia_semana}-${bloque.franja_asignada}`,
        nombre: ruta.nombre || 'Ruta',
        ciudad: ruta.ciudad || '',
        dia_semana: bloque.dia_semana,
        dia_label: bloque.dia_label,
        franja_asignada: bloque.franja_asignada,
        puntos: bloque.puntos || []
      });
    }
  }
  return tarjetas.sort(compararTarjetas);
}

const MisRutasSupervision = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rutas, setRutas] = useState([]);
  const [expandidas, setExpandidas] = useState({});
  const [filtroDia, setFiltroDia] = useState('todos');

  const tarjetas = useMemo(() => aplanarTarjetas(rutas), [rutas]);

  const diasConRutas = useMemo(() => {
    const set = new Set(tarjetas.map((t) => t.dia_semana));
    return DIAS_SEMANA.filter((d) => set.has(d.key));
  }, [tarjetas]);

  const tarjetasFiltradas = useMemo(() => {
    if (filtroDia === 'todos') return tarjetas;
    return tarjetas.filter((t) => t.dia_semana === filtroDia);
  }, [tarjetas, filtroDia]);

  const cargar = useCallback(async () => {
    if (user?.rol !== 'S') return;
    try {
      setLoading(true);
      setError('');
      const url = new URL(getApiUrl('SUPERVISION', 'RUTA_PROGRAMADA_MIS_ASIGNACIONES'));
      url.searchParams.set('username', user.username || '');
      const ctrl = new AbortController();
      const to = setTimeout(() => ctrl.abort(), 25000);
      let res;
      try {
        res = await fetch(url.toString(), {
          headers: { 'Content-Type': 'application/json' },
          signal: ctrl.signal
        });
      } finally {
        clearTimeout(to);
      }
      const data = await res.json();
      if (!res.ok || !data.success) {
        setRutas([]);
        setError(data.error || 'No se pudieron cargar sus rutas asignadas');
        return;
      }
      setRutas(data.data?.rutas || []);
      setExpandidas({});
    } catch (e) {
      setRutas([]);
      setError(
        e.name === 'AbortError'
          ? 'La carga tardó demasiado. Intente de nuevo.'
          : e.message || 'Error de conexión'
      );
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.rol !== 'S') {
      setError('No tienes permisos para acceder a esta sección');
      setLoading(false);
      return;
    }
    cargar();
  }, [user, cargar]);

  const toggleTarjeta = (key) => {
    setExpandidas((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (user?.rol !== 'S') {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Solo los supervisores pueden acceder a esta sección.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 sm:p-6 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Mis rutas</h1>
              <p className="mt-1 text-sm text-gray-600">
                Rutas y puntos asignados por día y turno.
              </p>
            </div>
            <button
              type="button"
              onClick={() => cargar()}
              disabled={loading}
              className={`flex items-center justify-center p-2 rounded-lg border transition-colors flex-shrink-0 ${
                loading
                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-transparent border-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Actualizar rutas"
              aria-label="Actualizar rutas"
            >
              <svg
                className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>

          {!loading && tarjetas.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Filtrar por día
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setFiltroDia('todos')}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                    filtroDia === 'todos'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Todos
                </button>
                {diasConRutas.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => setFiltroDia(d.key)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                      filtroDia === d.key
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
              <span className="ml-3 text-gray-600">Cargando rutas…</span>
            </div>
          ) : tarjetas.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
              <p className="text-gray-700 font-medium">No tiene rutas asignadas en el cronograma.</p>
              <p className="mt-1 text-sm text-gray-500">
                Si cree que debería tener rutas, contacte al administrador del cronograma.
              </p>
            </div>
          ) : tarjetasFiltradas.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
              <p className="text-gray-700 font-medium">No tiene rutas asignadas para este día.</p>
              <button
                type="button"
                onClick={() => setFiltroDia('todos')}
                className="mt-3 text-sm font-medium text-indigo-600 hover:text-indigo-800"
              >
                Ver todos los días
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {tarjetasFiltradas.map((t) => (
                <section
                  key={t.key}
                  className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggleTarjeta(t.key)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm sm:text-base font-bold text-gray-900 uppercase tracking-tight leading-snug">
                        {t.nombre}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                        <span className="inline-flex items-center rounded-md border border-gray-300 bg-gray-100 px-2 py-0.5 font-semibold uppercase text-gray-800">
                          {t.dia_label}
                        </span>
                        <IconoTurno franja={t.franja_asignada} />
                        {t.ciudad ? (
                          <>
                            <span className="text-gray-400" aria-hidden>
                              ·
                            </span>
                            <span className="font-medium uppercase text-gray-500">{t.ciudad}</span>
                          </>
                        ) : null}
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${
                        expandidas[t.key] ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {expandidas[t.key] && (
                    <div className="border-t border-gray-100 bg-gray-50/80 px-4 py-3 sm:px-5 sm:py-4">
                      <ul className="space-y-2">
                        {t.puntos.map((p) => {
                          const mapsUrl = urlMapaVirtual(p);
                          return (
                            <li
                              key={p.id_generado}
                              className="flex items-start justify-between gap-2 text-sm font-medium text-gray-800 leading-snug"
                            >
                              <span className="min-w-0 flex-1">{etiquetaPuntoConCodigo(p)}</span>
                              <IconoMapaVirtual url={mapsUrl} />
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default MisRutasSupervision;
